import { useState, useEffect } from "react";

export default function CincinnatiHeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Scroll-aware Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-sm border-b border-gray-100" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-[70px]">
          <a href="https://fastpassportcenter.com" className="flex items-center">
            <img
              src="https://staging.fastpassportcenter.com/wp-content/uploads/2026/02/Header.svg"
              alt="Fast Passport Center"
              className="h-9 w-auto transition-all"
              style={{ filter: scrolled ? "none" : "brightness(0) invert(1)" }}
            />
          </a>
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: "Services", href: "#services" },
              { label: "How It Works", href: "#how-it-works" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <a
              href="tel:8772530084"
              className={`hidden md:inline-flex items-center gap-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"
              }`}
            >
              <i className="ri-phone-line"></i>877-253-0084
            </a>
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] text-sm font-bold px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
            >
              Get Started
            </a>
            <button
              className="md:hidden flex items-center justify-center w-9 h-9"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <i
                className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"} ${
                  scrolled ? "text-[#1A365D]" : "text-white"
                }`}
              ></i>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-3">
            {[
              { label: "Services", href: "#services" },
              { label: "How It Works", href: "#how-it-works" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium text-[#1A365D] hover:text-[#F6C344] py-1"
              >
                {item.label}
              </a>
            ))}
            <a href="tel:8772530084" className="block text-sm font-semibold text-[#1A365D] py-1">
              <i className="ri-phone-line mr-1.5"></i>877-253-0084
            </a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Dramatic%20Cincinnati%20Ohio%20downtown%20skyline%20at%20golden%20hour%20with%20the%20historic%20John%20A.%20Roebling%20Suspension%20Bridge%20spanning%20the%20Ohio%20River%2C%20glass%20skyscrapers%20and%20modern%20office%20towers%20reflecting%20warm%20amber%20sunset%20light%2C%20the%20Bengals%20stadium%20visible%20along%20the%20waterfront%2C%20aerial%20perspective%20from%20Kentucky%20side%20showing%20the%20full%20city%20skyline%2C%20professional%20architectural%20cityscape%20photography%20with%20rich%20warm%20tones%20and%20vibrant%20sky%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=cincinnati-hero-2026-v2&orientation=landscape"
          alt="Cincinnati Ohio skyline at golden hour with the Roebling Bridge and Ohio River"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-16">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Left: Text Content */}
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-map-pin-fill text-[#F6C344] text-xs"></i>
                <span className="text-white/90 text-xs font-semibold">312 Walnut St — Downtown Cincinnati, OH</span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
                Emergency &amp; Expedited Passport Services in Cincinnati, OH
              </h1>
              <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto lg:mx-0 mb-8 leading-relaxed">
                SoMa-style startup founder at P&amp;G with a global supply-chain trip to Tokyo in 4 days? UC grad student heading to a research program in Berlin whose passport expired? CVG&rarr;Cancun family vacation departing Friday? We help Greater Cincinnati tri-state area travelers &mdash; from Fountain Square professionals to Northern Kentucky families to Mason commuters &mdash; get their passports fast. As a registered U.S. Department of State courier, we offer a best price guarantee and rates typically 30&ndash;100% lower than FedEx, Staples, and other passport courier resellers. As fast as 24 hours. A+ BBB rated.
              </p>
              <a
                href="https://fastpassportcenter.com/checkout/"
                className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap"
              >
                Start My Passport <i className="ri-arrow-right-line"></i>
              </a>

              <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-5 md:gap-7">
                {[
                  { icon: "ri-star-fill", label: "5.0 Reviews", highlight: true },
                  { icon: "ri-award-line", label: "BBB A+ Accredited" },
                  { icon: "ri-calendar-check-line", label: "20+ Years" },
                  { icon: "ri-shield-check-line", label: "Registered State Dept. Courier" },
                  { icon: "ri-price-tag-3-line", label: "Best Price Guarantee" },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-2 text-white/70 text-sm">
                    <i className={`${b.icon} text-[#F6C344] text-base`}></i>
                    <span>{b.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Office Card */}
            <div className="w-full lg:w-[360px] flex-shrink-0">
              <div className="bg-[#1A365D]/90 backdrop-blur-sm border border-white/15 rounded-xl overflow-hidden">
                {/* Office Photo */}
                <div className="relative">
                  <img
                    src="https://storage.readdy-site.link/project_files/5b15eb5b-af79-4c03-9ad0-73c6095d69a7/48297f2f-68d5-48d4-b308-ab2b2869a2d3_compressed_Cincinnati-fast-passport-center-office.webp"
                    alt="Fast Passport Center office at 312 Walnut St Cincinnati — modern glass skyscraper in downtown Cincinnati"
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A365D]/60 to-transparent pointer-events-none"></div>
                  <div className="absolute bottom-3 left-4">
                    <span className="inline-flex items-center gap-1.5 bg-[#F6C344] text-[#1A365D] text-xs font-bold px-3 py-1 rounded-full">
                      <i className="ri-building-line text-[10px]"></i>Cincinnati Office
                    </span>
                  </div>
                </div>

                {/* Office Info */}
                <div className="p-5">
                  <p className="text-white font-bold text-sm mb-3">Fast Passport Center — Cincinnati</p>
                  <div className="space-y-2.5 mb-4">
                    <div className="flex items-start gap-2.5">
                      <i className="ri-map-pin-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
                      <p className="text-white/70 text-xs leading-relaxed">312 Walnut St, Cincinnati, OH 45202<br /><span className="text-white/40">Near Fountain Square &amp; Roebling Bridge</span></p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <i className="ri-time-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
                      <div>
                        <p className="text-white/70 text-xs">Mon–Fri: 9:00 AM – 6:00 PM EST</p>
                        <p className="text-white/70 text-xs">Saturday: 10:00 AM – 2:00 PM EST</p>
                        <p className="text-white/40 text-xs italic mt-0.5">After-hours emergency available</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <i className="ri-phone-line text-[#F6C344] text-sm flex-shrink-0"></i>
                      <a href="tel:8772530084" className="text-white/70 text-xs hover:text-[#F6C344] transition-colors">(877) 253-0084</a>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <i className="ri-plane-line text-[#F6C344] text-sm flex-shrink-0"></i>
                      <p className="text-white/70 text-xs">CVG Airport — 13 miles south</p>
                    </div>
                  </div>
                  <a
                    href="https://maps.google.com/maps?daddr=312+Walnut+St,+Cincinnati,+OH+45202"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-xs px-4 py-2.5 rounded-md transition-all whitespace-nowrap"
                  >
                    <i className="ri-navigation-line"></i> Get Directions
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}